import { Stack } from '@mui/material';
import React from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/button/button';
import { TextButton } from '@/shared/components/ui/button/text-button/text-button';
import { TFormWizardSubmitProps } from '@/shared/components/ui/form-wizard/components/form-wizard-submit/form-wizard-submit.types';
import { useFormWizard } from '@/shared/components/ui/form-wizard/context/form-wizard-context';

export const FormWizardSubmit: React.FC<TFormWizardSubmitProps> = ({
  onClose,
  closeButtonLabel,
  submitButtonLabel,
  submitButtonOnly,
  formHasToBeDirty,
  submitButtonSize = 'large',
  displayButtonsAsFullwidth = true,
  skipable,
  skipSteps = 1,
  isLastStep,
}) => {
  const { t } = useTranslation();
  const { isSubmitting, isValid, isDirty } = useFormState();
  const { setLastFormIndex, setStep, step } = useFormWizard();
  const _closeButtonLabel = closeButtonLabel ?? t('common:cancel');

  const getSubmitButtonLabel = () => {
    if (submitButtonLabel) {
      return submitButtonLabel;
    }
    if (isLastStep) {
      return t('common:create');
    }
    return t('common:continue');
  };

  const handleSkip = () => {
    setLastFormIndex(step);
    setStep(step + skipSteps);
  };

  const disabledSubmitButton =
    isSubmitting || !isValid || (formHasToBeDirty && !isDirty);

  if (submitButtonOnly) {
    return (
      <Stack direction="row" justifyContent="flex-end">
        <Button
          type="submit"
          variant="primary"
          size={submitButtonSize}
          disabled={disabledSubmitButton}
        >
          {getSubmitButtonLabel()}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      marginTop="1rem"
      direction="row"
      gap={3}
      justifyContent="space-between"
    >
      <Button
        onClick={onClose}
        variant="secondary"
        fullWidth={displayButtonsAsFullwidth}
        size={submitButtonSize}
        disabled={isSubmitting}
      >
        {_closeButtonLabel}
      </Button>
      <Stack direction="row" gap="1rem" alignItems="center">
        {skipable && (
          <TextButton onClick={handleSkip}>{t('common:skip')}</TextButton>
        )}
        <Button
          fullWidth={displayButtonsAsFullwidth}
          type="submit"
          variant="primary"
          size={submitButtonSize}
          disabled={disabledSubmitButton}
        >
          {getSubmitButtonLabel()}
        </Button>
      </Stack>
    </Stack>
  );
};
